import clsx from 'clsx';
import {
  forwardRef,
  memo,
  type ButtonHTMLAttributes,
  type ElementType,
  type MouseEvent,
} from 'react';

import type { SharedButtonProps } from '../../types/SharedButtonProps';

import { useLinkElement } from '../../context/LinkNavigation/LinkNavigation';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import './Button.css';
import {
  isIconButtonHighlighted,
  resolveButtonIconSize,
  resolveButtonTextVariant,
} from './utils';

/** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
type ButtonPrivateHandoff = {
  mode?: 'icon';
};

export type ButtonProps = SharedButtonProps &
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    keyof SharedButtonProps | 'className'
  > & {
    __private?: ButtonPrivateHandoff;
  };

export const Button = memo(
  forwardRef<HTMLElement, ButtonProps>(function Button(props, forwardedRef) {
    const {
      __private,
      disabled,
      download,
      href,
      icon,
      isLoading = false,
      label,
      rel,
      selected,
      size = 'm',
      status,
      target,
      text,
      tooltip,
      type = 'button',
      variant: initialVariant,
      onClick,
      onToggle,
      ...rest
    } = props;

    const isLink = href !== undefined;
    const isToggle = onToggle !== undefined;
    const isTogglePattern = isToggle || selected !== undefined;
    const isNoop = !isLink && !onClick && !isTogglePattern && type !== 'submit';
    const isInactive = Boolean(disabled || isLoading);
    const isSoftDisabled = Boolean(tooltip?.text && isInactive);
    // Anchors can't be natively disabled, so inactive links use aria-disabled too.
    let isAriaDisabled: boolean | undefined = undefined;
    if (isNoop) {
      isAriaDisabled = isInactive;
    } else if (isSoftDisabled || (isLink && isInactive)) {
      isAriaDisabled = true;
    }
    const isNativeDisabled =
      !isNoop && !isLink && isInactive && !isSoftDisabled;
    const role = isNoop ? 'button' : undefined;
    // Drop the href while inactive so the link can't be followed. The remaining
    // anchor attributes are only meaningful alongside a live href.
    const resolvedHref = isLink && !isInactive ? href : undefined;
    const LinkElement = useLinkElement(resolvedHref, target);
    let as: ElementType = 'button';
    if (isLink) {
      as = resolvedHref !== undefined ? LinkElement : 'a';
    } else if (isNoop) {
      as = 'div';
    }
    const resolvedRel =
      resolvedHref !== undefined
        ? (rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined))
        : undefined;
    const anchorProps =
      resolvedHref !== undefined
        ? { href: resolvedHref, target, rel: resolvedRel, download }
        : undefined;

    const isIconMode = __private?.mode === 'icon';
    const iconSize = resolveButtonIconSize({ isIconMode, size });

    const variant = initialVariant ?? 'secondary';

    let iconKey = icon?.key;
    if (isLoading) {
      iconKey = 'loading';
    }
    const iconPosition = icon?.position ?? 'right';
    const iconFilled = Boolean(icon?.filled);

    const isSelected = Boolean(selected);
    const isHighlighted =
      isIconMode && !isTogglePattern && isIconButtonHighlighted(rest);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (isInactive) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isToggle) {
        if (type !== 'submit') {
          event.preventDefault();
          event.stopPropagation();
        }
        onToggle(!isSelected);
        return;
      }
      if (isLink) {
        // The anchor owns navigation; a handler can cancel it via preventDefault.
        onClick?.(event);
        return;
      }
      if (!onClick) {
        return;
      }
      if (type !== 'submit') {
        event.preventDefault();
        event.stopPropagation();
      }
      onClick(event);
    };

    return (
      <Tooltip direction={tooltip?.direction} tooltip={tooltip?.text}>
        <Layout
          align="center"
          aria-busy={isLoading ? true : undefined}
          aria-disabled={isAriaDisabled}
          aria-label={label}
          as={as}
          {...anchorProps}
          className={clsx(
            'eds-button',
            `eds-button--${variant}`,
            `eds-button--size-${size}`,
            isTogglePattern && status && `eds-button--status-${status}`,
            isIconMode && 'eds-button--icon-only',
            isHighlighted && 'eds-button--active',
            isSoftDisabled && 'eds-button--soft-disabled',
          )}
          direction="row"
          disabled={isNativeDisabled ? true : undefined}
          justify="center"
          role={role}
          spacing="xs"
          type={isLink ? undefined : type}
          onClick={isNoop ? undefined : handleClick}
          {...rest}
          aria-pressed={isTogglePattern ? isSelected : undefined}
          ref={forwardedRef}
        >
          {iconPosition === 'left' && iconKey ? (
            <Icon filled={iconFilled} icon={iconKey} size={iconSize} />
          ) : null}
          {!isIconMode && !isLoading && text ? (
            <Text variant={resolveButtonTextVariant({ size })}>{text}</Text>
          ) : null}
          {iconPosition === 'right' && iconKey ? (
            <Icon filled={iconFilled} icon={iconKey} size={iconSize} />
          ) : null}
        </Layout>
      </Tooltip>
    );
  }),
);

Button.displayName = 'Button';
